import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { LoanType } from '@apply/constants';

import * as Store from '@apply/store';

import { Header } from '../Header';
import { BorrowerReview, PropertiesReview } from '@apply/ui';
import { alertModal, loginAlertModal, ShowIf, BackButtonWithStore, fonts } from '@apply/ui';
import {
    constructAgreementHtml,
    generalAcknowledgementTemplate,
    rentalAcknowledgementTemplate,
} from '../finalize/ReviewSubmission';
import WebFont from 'webfontloader';
import { useAuthentication } from '../LoginPage';
import { HomePageLink } from '../HomePageLink';

export interface ReviewPageProps {
    state?: Store.ApplyRootStateType;
    actions?: Actions;
    printMode?: boolean;
}

export interface Actions {
    loadStateFromApi?: (apiId: string, setCurrentPropertyId?: string, token?: string) => Promise<boolean | undefined>;
    onUpdateEsign?: (esign: Store.BorrowerStore.Types.Esign) => void;
    saveStateToApi?: (apiId: string, complete: boolean, token: string) => Promise<Store.BorrowerStore.Types.IdAndToken>;
    onAddProperty: () => void;
    onRemoveProperty: (propertyGuid: string) => void;
    onNeedIpAddress?: () => Promise<string>;
}

export const ReviewPage: React.FC<ReviewPageProps> = ({ state, actions, printMode }) => {
    const { logout } = useAuthentication();
    const { apiId } = useParams<{ apiId: string }>();
    const { search, pathname } = useLocation();
    const params = new URLSearchParams(search);
    const token = params.get('token');
    const hideHeader = params.get('hideheader') ?? false;
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string>('');
    const [formCompleted, setFormCompleted] = useState<boolean>(false); // intentionally named different because it does not redirect.
    const [failedOnPropertyAdd, setFailedOnPropertyAdd] = useState<boolean>(false);

    React.useEffect(() => {
        (async () => {
            const fontNames = fonts.map((font) => font.name);
            WebFont.load({
                google: {
                    families: fontNames,
                },
            });
        })();
    }, []);
    useEffect(() => {
        let canceled = false;

        if (apiId) {
            setLoading(true);
            actions
                .loadStateFromApi(apiId, null, token)
                .then((complete) => {
                    if (complete) {
                        setFormCompleted(complete);
                    }
                })
                .catch((err) => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoadError(err);
                    }
                })
                .finally(() => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoading(false);
                    }
                });
        }
        return () => {
            canceled = true;
        };
    }, [apiId, token, actions]);

    useEffect(() => {
        if (failedOnPropertyAdd) {
            actions.onRemoveProperty(state.properties.currentPropertyId);
            setFailedOnPropertyAdd(false);
        }
    }, [failedOnPropertyAdd, state.properties.currentPropertyId, actions]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, pathname]);

    const onSubmitClick = () => {
        setLoading(true);
        actions
            .saveStateToApi(apiId, false, token)
            .then((idAndToken) => {
                setLoading(false);
                navigate(`/finalize/${idAndToken.id}${idAndToken.token ? '?token=' + idAndToken.token : ''}`, { replace: true });
                navigate(`/finalize/${idAndToken.id}${idAndToken.token ? '?token=' + idAndToken.token : ''}`);
            })
            .catch((err) => {
                setLoading(false);
                setLoadError(err);
            });
    };

    const handleAddAnotherProperty = async () => {
        setLoading(true);
        actions.onAddProperty();
        const destination = `/property/${apiId}?adding=true${token ? '&token=' + token : ''}`;
        actions
            .saveStateToApi(apiId, false, token)
            .then(() => {
                navigate(destination);
            })
            .catch((err) => {
                setFailedOnPropertyAdd(true);
                setLoadError(err);
                setLoading(false);
            });
    };

    if (loading) {
        return (
            <div
                className='overlay'
                data-testid='review-page-loading'
            >
                <div className='overlay__inner'>
                    <div className='overlay__content'>
                        <span className='spinner'></span>
                        <span className='spinner-text'>Loading</span>
                    </div>
                </div>
            </div>
        );
    }

    const { typedName, title, date, acknowledgeFontName, ipAddress } = state.borrower.esign;
    return (
        <>
            <ShowIf showIf={!hideHeader}>
                <Header printMode={printMode} />
            </ShowIf>
            <main>
                <ShowIf showIf={!printMode}>
                    <HomePageLink />
                </ShowIf>
                <section className='summary'>
                    <ShowIf
                        showIf={
                            printMode ||
                            (state?.borrower?.loanType !== LoanType.SubNoteConstruction &&
                                state?.borrower?.loanType !== LoanType.SubNoteFixFlip)
                        }
                    >
                        <BorrowerReview
                            borrower={state?.borrower}
                            apiId={apiId}
                            printMode={printMode}
                            formCompleted={formCompleted}
                            token={token}
                            collapsable={true}
                        />
                    </ShowIf>
                    <PropertiesReview
                        properties={state?.properties}
                        loanType={state?.borrower?.loanType}
                        apiId={apiId}
                        printMode={printMode}
                        formCompleted={formCompleted}
                        token={token}
                        collapsable={true}
                    />
                    <ShowIf
                        showIf={
                            !printMode &&
                            !formCompleted &&
                            state?.borrower?.loanType !== LoanType.SubNoteConstruction &&
                            state?.borrower?.loanType !== LoanType.SubNoteFixFlip
                        }
                    >
                        <div
                            className='row'
                            style={{
                                display: 'flex',
                                justifyContent: 'space-around',
                            }}
                        >
                            <div className='add-property-container'>
                                <div>
                                    <button
                                        onClick={handleAddAnotherProperty}
                                        className='button'
                                    >
                                        ADD ANOTHER PROPERTY
                                    </button>
                                </div>
                                <div style={{ textAlign: 'left' }}>
                                    You can Add Another Property to this <u>application</u> but it&nbsp;
                                    <strong>must also be owned by {state.borrower?.entity?.name}</strong>, otherwise start a new
                                    application
                                </div>
                            </div>
                            <div className='add-property-container'>
                                <div
                                    style={{
                                        textAlign: 'left',
                                        margin: 'auto',
                                    }}
                                >
                                    Please review the properties above before submitting your application. To correct any
                                    information, please <strong>use the edit button</strong> above in the property's header.
                                </div>
                            </div>
                        </div>
                    </ShowIf>
                </section>
                {printMode && typedName && title && date && acknowledgeFontName && (
                    <div className='row'>
                        <div
                            className='columns'
                            style={{
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                                paddingTop: '20px',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: constructAgreementHtml(
                                    state.borrower.loanType == LoanType.Rental
                                        ? rentalAcknowledgementTemplate
                                        : generalAcknowledgementTemplate,
                                    typedName,
                                    title,
                                    date,
                                    acknowledgeFontName,
                                    ipAddress
                                ),
                            }}
                        ></div>
                    </div>
                )}
                <ShowIf showIf={!printMode && !formCompleted}>
                    <div className='button-group'>
                        <BackButtonWithStore />
                        <button
                            id='finalizeButton'
                            className='button'
                            style={{ width: '100%' }}
                            onClick={onSubmitClick}
                            disabled={false}
                        >
                            Finalize
                        </button>
                    </div>
                </ShowIf>
            </main>
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    state,
});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            loadStateFromApi: (apiId: string, setCurrentPropertyId?: string, token?: string) =>
                dispatch(Store.CrossStore.loadStateFromApi(apiId, setCurrentPropertyId, token)),
            onUpdateEsign: (esign: Store.BorrowerStore.Types.Esign) => dispatch(Store.BorrowerStore.Actions.updateEsign(esign)),
            saveStateToApi: (apiId: string, complete = false, token: string) =>
                dispatch(Store.CrossStore.saveStateToApi(apiId, complete, token)),
            onAddProperty: () => dispatch(Store.PropertiesStore.Actions.addProperty()),
            onRemoveProperty: (guid: string) => dispatch(Store.PropertiesStore.Actions.removeProperty(guid)),
            onNeedIpAddress: () => dispatch(Store.CrossStore.getIpAddress()),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewPage);
